import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export interface QuoteIntakeOutOfScopeProps {
  /** The assistant's own explanation of why this is not covered. */
  message: string;
  onBack: () => void;
  /** Send it anyway: the manual form, triaged by a person. The primary action. */
  onSendAnyway: () => void;
  /** Lets the consumer push back — see the escape-hatch note below. */
  onContact: () => void;
}

/**
 * Terminal state when the conversation establishes the need is neither
 * notarial nor accounting work — a municipal habilitación, a passport renewal
 * (spec add326e0 §4e).
 *
 * No `quote_request` row is created. That is the point: half of the RFQs stuck
 * unrouted for weeks were work the platform does not sell, and telling the
 * consumer immediately beats creating a request no professional can service
 * and no admin can route.
 *
 * The escape hatch is not decoration. A false out-of-scope turns away real
 * business, which is strictly worse than routing slowly, so the consumer is
 * always offered a way to say the model got it wrong — and that way is the
 * FORM, as the primary action. "Escribinos" was the only way out before, which
 * asks the consumer to start again in another channel, and in an environment
 * without the chat widget did nothing at all.
 */
export function QuoteIntakeOutOfScope({
  message,
  onBack,
  onSendAnyway,
  onContact,
}: QuoteIntakeOutOfScopeProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Card className="border-border/80 shadow-sm dark:border-border/80 dark:bg-secondary/35">
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Info className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-semibold text-foreground">
              {t("requests.user.quoteIntakeOutOfScopeTitle")}
            </p>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{message}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={onSendAnyway}>
            {t("requests.user.quoteIntakeOutOfScopeSendAnyway")}
          </Button>
          <Button type="button" variant="outline" onClick={onBack}>
            {t("requests.user.quoteIntakeOutOfScopeBack")}
          </Button>
          <Button type="button" variant="ghost" onClick={onContact}>
            {t("requests.user.quoteIntakeOutOfScopeContact")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
